import React, { useEffect, useState } from 'react';
import api, { getApiBaseUrl } from '../../../../utils/apiservice';

interface SponsorData {
  id: number;
  name: string;
  imageUrl: string | null;
  isExternalImage: boolean;
  websiteUrl: string | null;
  type: string;
  sortOrder: number;
  raceId: string;
}

const SponsorsSection = ({ t, raceId }: { t: any; raceId?: string | null }) => {
  const [sponsors, setSponsors] = useState<SponsorData[]>([]);
  const [partners, setPartners] = useState<SponsorData[]>([]);
  const [loading, setLoading] = useState(true);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const fetchSponsors = async () => {
      let apiSponsors: SponsorData[] = [];
      let apiPartners: SponsorData[] = [];
      let jsonSponsors: SponsorData[] = [];
      let jsonPartners: SponsorData[] = [];

      // Load API sponsors if raceId is available
      if (raceId) {
        try {
          const res = await api.get(`/api/races/${raceId}/sponsors`);
          const data: SponsorData[] = res.data;
          apiSponsors = data.filter((s) => s.type === 'Sponsor');
          apiPartners = data.filter((s) => s.type === 'Partner');
        } catch (err) {
          console.warn('Failed to fetch sponsors from API:', err);
        }
      }
      // Merge: API sponsors first, then JSON sponsors (excluding names already in API data)
      const apiSponsorNames = new Set(apiSponsors.map((s) => s.name.toLowerCase()));
      const apiPartnerNames = new Set(apiPartners.map((p) => p.name.toLowerCase()));

      const mergedSponsors = [
        ...apiSponsors,
        ...jsonSponsors.filter((s) => !apiSponsorNames.has(s.name.toLowerCase())),
      ];
      const mergedPartners = [
        ...apiPartners,
        ...jsonPartners.filter((p) => !apiPartnerNames.has(p.name.toLowerCase())),
      ];

      setSponsors(mergedSponsors);
      setPartners(mergedPartners);
      setLoading(false);
    };
    fetchSponsors();
  }, [raceId]);

  if (loading) return <div>Loading sponsors...</div>;
  if (sponsors.length === 0 && partners.length === 0) return null;

  const getImageSrc = (s: SponsorData) => {
    if (!s.imageUrl) return '';
    if (s.isExternalImage) return s.imageUrl;
    // For uploaded images, imageUrl is like /uploads/sponsors/xxx.png — prefix with API base
    return `${getApiBaseUrl()}${s.imageUrl}`;
  };

  return (
    <>
      {sponsors.length > 0 && (
        <section id="sponsors" className="sponsors-section">
          <h2>{t.sponsors}</h2>
          <div className="sponsors-logos">
            {sponsors.map((s) => (
              <a
                key={s.id}
                href={s.websiteUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="sponsor-link"
              >
                <img
                  src={getImageSrc(s)}
                  alt={s.name}
                  className="sponsor-logo"
                  draggable={false}
                />
              </a>
            ))}
          </div>
        </section>
      )}
      {partners.length > 0 && (
        <section id="partners" className="sponsors-section">
          <h2>{t.partners}</h2>
          <div className="sponsors-logos">
            {partners.map((p) => (
              <a
                key={p.id}
                href={p.websiteUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="sponsor-link"
              >
                <img
                  src={getImageSrc(p)}
                  alt={p.name}
                  className="sponsor-logo"
                  draggable={false}
                />
              </a>
            ))}
          </div>
        </section>
      )}
    </>
  );
};

export default SponsorsSection;
